<template>
    <div class="mx-3">
        <v-card>
            <v-overlay absolute v-if="loading">
                <v-progress-circular
                    indeterminate
                    :size="50"
                    :width="5"
                    color="primary"
                >
                </v-progress-circular>
            </v-overlay>
            <v-row dense class="d-flex align-center mb-2">
                <v-col cols="4">
                    <v-card-title class="pl-4">{{ title }}</v-card-title>
                </v-col>

                <v-col cols="4">
                    <v-text-field
                    :label="$t('Search') + '...'"
                    prepend-icon="mdi-magnify"
                    v-debounce:300ms="changeFilter"
                    v-model="filter.search"
                    hide-details
                    class="pt-0"
                    />
                </v-col>

                <v-col cols="3">
                    <v-select
                    :label="$t('Type')"
                    :items="typeList"
                    v-model="filter.type"
                    item-text="text"
                    item-value="id"
                    clearable
                    @change="changeFilter"
                    hide-details
                    class="pt-0"
                    />
                </v-col>

                <v-col cols="1" class="text-right">
                    <v-btn
                    small
                    color="primary"
                    class="mt-1"
                    @click.prevent="addVariable"
                    >
                    <v-icon left>mdi-plus</v-icon>
                    {{ $t('Add') }}
                    </v-btn>
                </v-col>
            </v-row>
            <v-card-text class="pa-0">
                <simple-table
                    :ref="variablesTableKey"
                    :tableConfig="tableConfig"
                    :fetchData="getVariables"
                    :tableData="tableData"
                ></simple-table>
            </v-card-text>
        </v-card>
    </div>
</template>
<script>
import SimpleTable from '@/components/base/SimpleTable.vue';
import Utils from '@/ecm/common/Utils';
import VariableService from '@/services/VariableService';
import { VariableType } from '@/ecm/variables/VariableType';
import Variable from '@/views/administration/variables/Variable.vue';
import FieldType from '@/ecm/variables/FieldType';
export default {
    name: 'Variables',
    components: {
        SimpleTable,
        Variable
    },
    data() {
        return {
            title: this.$t('Variables'),
            loading: false,
            variablesTableKey: 'variablesTableKey',
            filter: {
                type: null,
                search: null,
            },
            typeList: VariableType.map(item => ({
                ...item,
                text: this.$t(item.text)
            })),
            dataSource: [],
            childComponents: [],
            defaultPageSize: 50,
            tableData: []
        };
    },
    computed: {
        variablesTableInstance() {
            return this.$refs[this.variablesTableKey];
        },
        tableConfig() {
            const tableHeaders = [];
            tableHeaders.push({ title: 'Name', titleTranslation: 'Name', allowSorting: true });
            tableHeaders.push({ title: 'Code', titleTranslation: 'Code', allowSorting: true });
            tableHeaders.push({ title: 'Type', titleTranslation: 'Type', allowSorting: true });
            tableHeaders.push({ title: 'Value', titleTranslation: 'Value', allowSorting: true });

            return {
                dense: false,
                allowSelection: false,
                selectAllTitleTranslation: 'Variables',
                showActions: true,
                actionsWidth: '15%',
                showPagination: true,
                allowSorting: true,
                headers: tableHeaders,
                defaultPageSize: this.defaultPageSize
            };
        }
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            vm.childComponents = [Variable.name];
            if (from.name === Variable.name) {
                const savedPageSize = Number(sessionStorage.getItem('variablesPageSize'));

                if (vm.childComponents.includes(from.name) && savedPageSize) {
                    vm.defaultPageSize = savedPageSize;

                    vm.$nextTick(() => {
                        if (vm.variablesTableInstance) {
                            vm.variablesTableInstance.setPageSize(vm.defaultPageSize);
                        }
                    });
                }
            }
        });
    },

    beforeRouteLeave(to, from, next) {
        if (this.childComponents.includes(to.name) && this.variablesTableInstance) {
            const currentPageSize = this.variablesTableInstance.getCurrentPageSize();
            sessionStorage.setItem('variablesPageSize', String(currentPageSize));
        }
        next();
    },

    methods: {
        async getVariables(paginationSetting) {
            const request = {
                skip: paginationSetting ? (paginationSetting.page - 1) * paginationSetting.pageSize : 0,
                take: paginationSetting ? paginationSetting.pageSize : this.defaultPageSize,
                requireTotalCount: true,
                type: this.filter.type,
                searchFilter: this.filter.search
            };

            if (paginationSetting && paginationSetting.orderBy) {
                request.sort = JSON.stringify([{
                    selector: paginationSetting.orderBy,
                    desc: paginationSetting.desc
                }]);
            }
            this.loading = true;
            const response = await VariableService.getGridData(request);
            this.loading = false;

            if (!response) {
                return;
            }

            this.dataSource = response.data;
            this.tableData = this.transformTableData(response.data);
            return {
                totalItems: response.totalCount,
                totalPages: Math.ceil(response.totalCount / paginationSetting.pageSize),
                result: response.data
            };
        },
        transformTableData(rows) {
            return rows.map(row => {
                const tableFields = [
                    { title: row.Name, },
                    { title: row.Code, },
                    { title: this.$t(VariableType.find(item => item.id === row.Type).text) },
                    { title: row.Value, function: () => this.displayValuesInUserFormat(row.Value, row.Type) },
                ];
                const tableFieldActions = [
                    { icon: 'mdi-pencil', color: 'blue', function: () => this.editVariable(row.Id), translation: 'Edit', redirect: false },
                    { icon: 'mdi-delete', color: 'red', function: () => this.deleteVariable(row), translation: 'Delete', redirect: false },
                ];

                return {
                    id: row.Id,
                    fields: tableFields,
                    actions: tableFieldActions,
                };
            });
        },
        addVariable() {
            this.$router.push('/administration/variables/new');
        },
        changeFilter() {
            this.variablesTableInstance.resetPagination();
        },
        editVariable(id) {
            this.$router.push(`/administration/variables/${id}`);
        },
        deleteVariable(variable) {
            this.$confirm(
                this.$t('Site.Variables.DeleteConfirm').replace('{0}', variable.Name),
                {
                    title: this.$t('Site.Variables.DeleteTitle'),
                    icon: 'mdi-warning',
                    color: 'red',
                    buttonTrueText: this.$t('Delete'),
                    buttonFalseText: this.$t('Cancel'),
                }
            ).then((res) => {
                if (res == 1) {
                    this.loading = true;
                    VariableService.delete(variable.Id)
                        .then(() => {
                            Utils.toast.success(this.$t('Site.Variables.DeleteSuccess').replace('{0}', variable.Name));
                            this.variablesTableInstance.loadData();
                        })
                        .catch((error) => {
                            Utils.toast.error(this.$t('Site.Variables.DeleteError').replace('{0}', variable.Name).replace('{1}', error.response.data.detail));
                        }).then(() => {
                            this.loading = false;
                        });
                }
            });
        },
        displayValuesInUserFormat(val, type) {
            switch (type) {
            case FieldType.Numeric: {
                const userDecSep = this.$store.getters['account/user'].MetricDecimalSep;
                const userThouSep = this.$store.getters['account/user'].MetricThousandSep;

                return Utils.number.toUserFormat(val, userDecSep, userThouSep);
            }
            case FieldType.DateTime:
                return Utils.date.toUserFormatFromIso(val, false);
            default:
                return val;
            }
        }
    },
};
</script>
<style></style>
